/**
 * EventEmitter - Generic event system base class
 * 
 * Use this as a base class for any system that needs to emit events.
 * Supports typed events with TypeScript generics.
 * 
 * @example
 * ```typescript
 * type MyEvents = {
 *   change: { oldValue: number; newValue: number };
 *   error: { message: string };
 * };
 * 
 * class MyClass extends EventEmitter<MyEvents> {
 *   doSomething() {
 *     this.emit("change", { oldValue: 1, newValue: 2 });
 *   }
 * }
 * 
 * const instance = new MyClass();
 * instance.on("change", (data) => console.log(data.newValue));
 * ```
 */

/**
 * Generic event handler type
 */
export type EventHandler<T = unknown> = (data: T) => void;

/**
 * Event map type constraint - keys are event names, values are event data types
 */
export type EventMap = Record<string, unknown>;

/**
 * EventEmitter base class - provides typed event subscription and emission
 */
export class EventEmitter<TEvents extends EventMap = EventMap> {
    private _listeners: Map<keyof TEvents, Set<EventHandler<unknown>>> = new Map();

    /**
     * Subscribe to an event
     * @param event Event name
     * @param handler Handler function
     * @returns Unsubscribe function
     */
    public on<K extends keyof TEvents>(
        event: K,
        handler: EventHandler<TEvents[K]>
    ): () => void {
        if (!this._listeners.has(event)) {
            this._listeners.set(event, new Set());
        }
        this._listeners.get(event)!.add(handler as EventHandler<unknown>);
        
        // Return unsubscribe function
        return () => this.off(event, handler);
    }

    /**
     * Subscribe to an event (once only)
     * @param event Event name
     * @param handler Handler function
     */
    public once<K extends keyof TEvents>(
        event: K,
        handler: EventHandler<TEvents[K]>
    ): () => void {
        const wrapper: EventHandler<TEvents[K]> = (data) => {
            this.off(event, wrapper);
            handler(data);
        };
        return this.on(event, wrapper);
    }

    /**
     * Unsubscribe from an event
     * @param event Event name
     * @param handler Handler function to remove
     */
    public off<K extends keyof TEvents>(
        event: K,
        handler: EventHandler<TEvents[K]>
    ): void {
        const handlers = this._listeners.get(event);
        if (handlers) {
            handlers.delete(handler as EventHandler<unknown>);
            if (handlers.size === 0) {
                this._listeners.delete(event);
            }
        }
    }

    /**
     * Emit an event to all subscribers
     * @param event Event name
     * @param data Event data
     */
    protected emit<K extends keyof TEvents>(event: K, data: TEvents[K]): void {
        const handlers = this._listeners.get(event);
        if (handlers) {
            for (const handler of handlers) {
                try {
                    handler(data);
                } catch (error) {
                    console.error(`Error in event handler for "${String(event)}":`, error);
                }
            }
        }
    }

    /**
     * Remove all listeners for a specific event or all events
     * @param event Optional event name. If omitted, removes all listeners.
     */
    public removeAllListeners<K extends keyof TEvents>(event?: K): void {
        if (event !== undefined) {
            this._listeners.delete(event);
        } else {
            this._listeners.clear();
        }
    }

    /**
     * Get the number of listeners for an event
     * @param event Event name
     */
    public listenerCount<K extends keyof TEvents>(event: K): number {
        return this._listeners.get(event)?.size ?? 0;
    }

    /**
     * Check if there are any listeners for an event
     * @param event Event name
     */
    public hasListeners<K extends keyof TEvents>(event: K): boolean {
        return this.listenerCount(event) > 0;
    }

    /**
     * Dispose all listeners
     */
    protected disposeListeners(): void {
        this._listeners.clear();
    }
}

/**
 * Mixin for adding EventEmitter functionality to existing classes
 * Use when you can't extend EventEmitter directly
 */
export function createEventEmitter<TEvents extends EventMap>() {
    return new EventEmitter<TEvents>();
}
